import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { adminApi } from '../../lib/api';
import {
  formatCurrency, formatDateTime, getPaymentStatusClass,
  PAYMENT_STATUS_LABELS, getTodayString, getDateDaysAgo,
} from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Badge } from '../../components/ui/badge';
import {
  Search, Download, ChevronLeft, ChevronRight, Plus,
  Banknote, Smartphone, Landmark, CreditCard, HelpCircle,
  CheckCircle2, Loader2, User,
} from 'lucide-react';
import { LoadingBlock, EmptyState } from '../../components/common/States';

const MANUAL_METHODS = [
  { key: 'cash',          label: 'Cash',          icon: Banknote,   color: 'text-emerald-300' },
  { key: 'upi',           label: 'UPI',           icon: Smartphone, color: 'text-blue-300' },
  { key: 'bank_transfer', label: 'Bank Transfer', icon: Landmark,   color: 'text-violet-300' },
  { key: 'card_pos',      label: 'Card (POS)',    icon: CreditCard, color: 'text-amber-300' },
  { key: 'other',         label: 'Other',         icon: HelpCircle, color: 'text-zinc-300' },
];

const MANUAL_METHOD_LABEL = Object.fromEntries(MANUAL_METHODS.map(m => [m.key, m.label]));

export default function PaymentsPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    date_from: getDateDaysAgo(30),
    date_to: getTodayString(),
    page: 1,
    limit: 20,
  });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (params.status === 'all') delete params.status;
      if (!params.search) delete params.search;
      const res = await adminApi.getPayments(params);
      setPayments(res.data.payments);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  return (
    <AdminShell
      title="Payments"
      actions={
        <div className="flex gap-2">
          <LogManualPaymentDialog onLogged={fetchPayments} />
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40 text-xs"
            onClick={() => adminApi.exportPayments(filters.date_from, filters.date_to)}
            data-testid="export-payments-button"
          >
            <Download size={13} /> Export CSV
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6F6F6F]" />
              <Input
                placeholder="Order ID, transaction ID..."
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
                className="bg-[#111111] border-[#2A2A2A] pl-9 text-sm"
                data-testid="payments-search-input"
              />
            </div>
            <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v, page: 1 }))}>
              <SelectTrigger className="bg-[#111111] border-[#2A2A2A] text-sm" data-testid="payments-status-filter">
                <SelectValue placeholder="Payment status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({...f, date_from: e.target.value, page: 1}))} className="bg-[#111111] border-[#2A2A2A] text-sm flex-1" />
              <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({...f, date_to: e.target.value, page: 1}))} className="bg-[#111111] border-[#2A2A2A] text-sm flex-1" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[#B9B9B9] text-sm"><span className="text-[#F2F2F2] font-medium">{total}</span> payments</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" disabled={filters.page <= 1} onClick={() => setFilters(f => ({...f, page: f.page-1}))} className="h-7 w-7 text-[#B9B9B9]"><ChevronLeft size={15} /></Button>
            <span className="text-[#B9B9B9] text-xs">{filters.page} / {pages || 1}</span>
            <Button variant="ghost" size="icon" disabled={filters.page >= pages} onClick={() => setFilters(f => ({...f, page: f.page+1}))} className="h-7 w-7 text-[#B9B9B9]"><ChevronRight size={15} /></Button>
          </div>
        </div>

        <div className="surface-card card-highlight rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-4"><LoadingBlock rows={8} /></div>
          ) : payments.length === 0 ? (
            <EmptyState title="No payments found" description="Try adjusting filters or log a manual payment." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#2A2A2A]">
                    {['Order ID', 'Customer', 'Amount', 'Method', 'Status', 'Transaction ID', 'Date', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs text-[#6F6F6F] uppercase tracking-wider font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E1E1E]">
                  {payments.map(p => {
                    const provider = (p.provider || '').toLowerCase();
                    const isManual = provider === 'manual';
                    const isGate   = provider === 'gate';
                    const rawMethod = p?.raw_response?.payment_method;
                    let methodLabel = '—';
                    if (isManual && rawMethod) methodLabel = MANUAL_METHOD_LABEL[rawMethod] || rawMethod;
                    else if (isGate && rawMethod) methodLabel = `Gate · ${rawMethod === 'static_qr' ? 'QR' : rawMethod.charAt(0).toUpperCase() + rawMethod.slice(1)}`;
                    else if (provider) methodLabel = provider.charAt(0).toUpperCase() + provider.slice(1);
                    return (
                      <tr key={p._id} className="table-row-hover cursor-pointer transition-colors" onClick={() => navigate(`/admin/checkins/${p.checkin_id}`)} data-testid={`payment-row-${p.order_id}`}>
                        <td className="px-4 py-3 text-mono text-[#D4A843] text-xs max-w-[150px] truncate">
                          {p.order_id}
                          {isManual && <Badge className="ml-2 bg-amber-900/40 text-amber-300 border-amber-700 text-[10px]">Manual</Badge>}
                          {isGate && <Badge className="ml-2 bg-blue-900/40 text-blue-300 border-blue-700 text-[10px]">Gate</Badge>}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-[#F2F2F2] font-medium">{p.customer_name || '—'}</p>
                          <p className="text-[#6F6F6F] text-xs">{p.phone}</p>
                        </td>
                        <td className="px-4 py-3 text-[#D4A843] font-medium">{formatCurrency(p.amount)}</td>
                        <td className="px-4 py-3 text-[#B9B9B9] text-xs">{methodLabel}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getPaymentStatusClass(p.status)}`}>
                            {PAYMENT_STATUS_LABELS[p.status] || p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-mono text-[#6F6F6F] text-xs max-w-[160px] truncate">{p.transaction_id || '—'}</td>
                        <td className="px-4 py-3 text-[#B9B9B9] text-xs">{formatDateTime(p.created_at)}</td>
                        <td className="px-4 py-3">
                          <Button size="sm" variant="ghost" className="text-xs text-[#B9B9B9] h-6 px-2"
                            onClick={e => { e.stopPropagation(); navigate(`/admin/checkins/${p.checkin_id}`); }}>
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Manual Payment Dialog — search check-in, pick method, log payment
// ─────────────────────────────────────────────────────────────────────
function LogManualPaymentDialog({ onLogged }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);    // selected check-in doc
  const [method, setMethod]   = useState('cash');
  const [amount, setAmount]   = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote]       = useState('');
  const [busy, setBusy]       = useState(false);
  const [done, setDone]       = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery(''); setResults([]); setSelected(null);
      setMethod('cash'); setAmount(''); setReference(''); setNote('');
      setBusy(false); setDone(null);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (selected) return;          // searching is paused once a check-in is chosen
    if (!query || query.trim().length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminApi.getCheckIns({
          search: query.trim(),
          payment_status: 'pending',
          limit: 8,
        });
        setResults(res.data.checkins || []);
      } catch {
        setResults([]);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [query, selected]);

  const pickCheckin = (c) => {
    setSelected(c);
    setAmount(String(c.total_amount || 0));
    setResults([]);
    setQuery('');
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!selected) { toast.error('Select a check-in first'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setBusy(true);
    try {
      const res = await adminApi.createManualPayment({
        checkin_id:     selected._id,
        payment_method: method,
        amount:         amt,
        reference_id:   reference.trim() || null,
        note:           note.trim() || null,
      });
      setDone(res.data.payment);
      toast.success('Manual payment logged');
      onLogged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to log manual payment');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="btn-gold gap-1.5 text-xs" data-testid="btn-log-manual-payment">
          <Plus size={13} /> Log Manual Payment
        </Button>
      </DialogTrigger>

      <DialogContent className="bg-[#0F0F0F] border-[#262626] text-[#F2F2F2] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#F2F2F2] font-['EB_Garamond']">
            {done ? 'Payment Recorded' : 'Log Manual Payment'}
          </DialogTitle>
          <DialogDescription className="text-[#B9B9B9] text-xs">
            {done
              ? 'The check-in has been marked as paid and wallet points credited.'
              : 'Record a payment collected offline (cash, UPI, bank, POS). The check-in will be marked paid immediately.'}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-3 py-2" data-testid="manual-payment-success">
            <div className="bg-[#1A1A1A] rounded-xl p-5 text-center border border-emerald-700/40">
              <CheckCircle2 size={32} className="text-emerald-300 mx-auto mb-2" />
              <p className="text-[#6F6F6F] text-xs uppercase tracking-wider mb-1">Order</p>
              <p className="font-mono text-[#D4A843] text-base">{done.order_id}</p>
              <p className="text-[#F2F2F2] text-2xl font-semibold mt-3">₹{Number(done.amount).toLocaleString('en-IN')}</p>
              <p className="text-[#B9B9B9] text-xs mt-1">via {MANUAL_METHOD_LABEL[done?.raw_response?.payment_method] || done?.raw_response?.payment_method}</p>
            </div>
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button onClick={() => { setDone(null); setSelected(null); setQuery(''); setAmount(''); setReference(''); setNote(''); }}
                variant="outline" className="bg-transparent border-[#3A2F16] text-[#B9B9B9]" data-testid="btn-log-another">
                Log another
              </Button>
              <Button onClick={() => setOpen(false)} className="btn-gold" data-testid="btn-close-manual">Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 py-1" data-testid="manual-payment-form">
            {/* Check-in picker */}
            {!selected ? (
              <div className="space-y-2">
                <Label className="text-[#D4A843] text-xs">Find Check-In (unpaid)</Label>
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6F6F6F]" />
                  <Input
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Phone, name, or reference id..."
                    className="bg-[#1A1A1A] border-[#3A2F16] pl-9 text-[#F2F2F2]"
                    autoFocus
                    data-testid="manual-checkin-search"
                  />
                </div>
                {searching && (
                  <p className="text-[#6F6F6F] text-xs flex items-center gap-1.5">
                    <Loader2 size={11} className="animate-spin" /> Searching…
                  </p>
                )}
                {!searching && query.trim().length >= 2 && results.length === 0 && (
                  <p className="text-[#6F6F6F] text-xs">No unpaid check-ins match.</p>
                )}
                {results.length > 0 && (
                  <div className="border border-[#262626] rounded-lg divide-y divide-[#1E1E1E] max-h-56 overflow-y-auto" data-testid="manual-checkin-results">
                    {results.map(c => (
                      <button type="button" key={c._id}
                        onClick={() => pickCheckin(c)}
                        className="w-full text-left px-3 py-2 hover:bg-[#1A1A1A] transition-colors"
                        data-testid={`manual-checkin-pick-${c._id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-[#F2F2F2] text-sm font-medium truncate">{c.customer_name || c.phone || 'Guest'}</p>
                            <p className="text-[#6F6F6F] text-xs truncate">
                              {c.phone} · {c.guests} guest{c.guests === 1 ? '' : 's'} · {c.visit_date}
                            </p>
                            {c.reference_id && (
                              <p className="text-[#6F6F6F] text-[10px] font-mono mt-0.5">REF {c.reference_id}</p>
                            )}
                          </div>
                          <p className="text-[#D4A843] text-sm font-medium whitespace-nowrap">
                            ₹{Number(c.total_amount || 0).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#1A1A1A] rounded-lg p-3 border border-[#3A2F16]" data-testid="manual-checkin-selected">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[#6F6F6F] text-[10px] uppercase tracking-wider">Selected check-in</p>
                    <p className="text-[#F2F2F2] font-medium mt-0.5 flex items-center gap-1.5">
                      <User size={12} className="text-[#D4A843]" /> {selected.customer_name || selected.phone}
                    </p>
                    <p className="text-[#B9B9B9] text-xs mt-0.5">
                      {selected.phone} · {selected.guests} guest{selected.guests === 1 ? '' : 's'} · {selected.visit_date}
                    </p>
                    {selected.reference_id && (
                      <p className="text-[#6F6F6F] text-[10px] font-mono mt-0.5">REF {selected.reference_id}</p>
                    )}
                  </div>
                  <Button type="button" size="sm" variant="ghost"
                    onClick={() => { setSelected(null); setAmount(''); }}
                    className="text-[#6F6F6F] hover:text-[#F2F2F2] h-6 px-2 text-xs"
                    data-testid="btn-change-checkin">
                    Change
                  </Button>
                </div>
              </div>
            )}

            {selected && (
              <>
                {/* Method picker */}
                <div className="space-y-2">
                  <Label className="text-[#D4A843] text-xs">Payment Method</Label>
                  <RadioGroup value={method} onValueChange={setMethod}
                    className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {MANUAL_METHODS.map(m => (
                      <label key={m.key}
                        className={`cursor-pointer rounded-lg border p-2 text-center transition ${
                          method === m.key
                            ? 'bg-[#332710] border-[#D4A843]'
                            : 'bg-[#1A1A1A] border-[#3A2F16] hover:bg-[#222]'
                        }`}
                        data-testid={`manual-method-${m.key}`}>
                        <RadioGroupItem value={m.key} className="sr-only" />
                        <m.icon size={16} className={`${m.color} mx-auto mb-1`} />
                        <p className="text-[#F2F2F2] text-xs">{m.label}</p>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {/* Amount */}
                <div className="space-y-1">
                  <Label htmlFor="manual-amt" className="text-[#D4A843] text-xs">Amount Collected (₹)</Label>
                  <Input id="manual-amt" type="number" min={1} step="1" value={amount}
                    onChange={e => setAmount(e.target.value)}
                    className="bg-[#1A1A1A] border-[#3A2F16] text-[#F2F2F2]"
                    data-testid="manual-amount-input"
                    required />
                  <p className="text-[#6F6F6F] text-[10px]">
                    Check-in total is ₹{Number(selected.total_amount || 0).toLocaleString('en-IN')}.
                  </p>
                </div>

                {/* Reference */}
                <div className="space-y-1">
                  <Label htmlFor="manual-ref" className="text-[#D4A843] text-xs">
                    Reference / UTR (optional)
                  </Label>
                  <Input id="manual-ref" value={reference}
                    onChange={e => setReference(e.target.value)}
                    placeholder="UTR no., receipt no., last-4 of card…"
                    className="bg-[#1A1A1A] border-[#3A2F16] text-[#F2F2F2]"
                    data-testid="manual-reference-input" />
                </div>

                {/* Notes */}
                <div className="space-y-1">
                  <Label htmlFor="manual-note" className="text-[#D4A843] text-xs">Notes (optional)</Label>
                  <Input id="manual-note" value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="e.g. Collected by Rohit at Gate 1"
                    className="bg-[#1A1A1A] border-[#3A2F16] text-[#F2F2F2]"
                    data-testid="manual-note-input" />
                </div>
              </>
            )}

            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}
                className="bg-transparent border-[#3A2F16] text-[#B9B9B9]" data-testid="btn-cancel-manual">
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !selected || !amount}
                className="btn-gold" data-testid="btn-submit-manual">
                {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
